import { useEffect } from "react";
import { ProductLine, ProductVariant } from "@/data/productLines";

interface ProductSEOProps {
  product: ProductLine;
  activeVariant?: ProductVariant;
}

const ProductSEO = ({ product, activeVariant }: ProductSEOProps) => {
  useEffect(() => {
    // Set document title
    const variantSuffix = activeVariant ? ` ${activeVariant.name}` : "";
    document.title = `${product.name}${variantSuffix} - ${product.tagline} | SDC Gulf`;

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", product.seoDescription || product.description);
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content = product.seoDescription || product.description;
      document.head.appendChild(meta);
    }

    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute("content", product.seoKeywords || "");
    } else if (product.seoKeywords) {
      const meta = document.createElement("meta");
      meta.name = "keywords";
      meta.content = product.seoKeywords;
      document.head.appendChild(meta);
    }

    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const ogImage = document.querySelector('meta[property="og:image"]');

    if (ogTitle) {
      ogTitle.setAttribute("content", `${product.name} - ${product.tagline}`);
    }
    if (ogDescription) {
      ogDescription.setAttribute("content", product.seoDescription || product.description);
    }
    if (ogImage) {
      ogImage.setAttribute("content", product.image);
    }

    // Add JSON-LD structured data
    const existingJsonLd = document.querySelector('script[type="application/ld+json"][data-product]');
    if (existingJsonLd) {
      existingJsonLd.remove();
    }

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": `${product.name}${variantSuffix}`,
      "brand": {
        "@type": "Brand",
        "name": product.brand
      },
      "description": product.seoDescription || product.description,
      "image": product.image,
      "category": product.powerType === "electric" ? "Electric Vehicle" : "Gas Vehicle",
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "SAR",
        "seller": {
          "@type": "Organization",
          "name": "SDC Gulf"
        }
      }
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-product", product.id);
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    // Cleanup
    return () => {
      // Reset title on unmount
      document.title = "SDC Gulf - Sustainable Mobility Solutions";
      
      // Remove JSON-LD
      const jsonLdScript = document.querySelector(`script[data-product="${product.id}"]`);
      if (jsonLdScript) {
        jsonLdScript.remove();
      }
    };
  }, [product, activeVariant]);

  return null;
};

export default ProductSEO;
